'use client';

import { motion } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface ServiceCTAProps {
  heading?: string;
  subtext?: string;
}

export function ServiceCTA({
  heading = 'Ready to Get Started?',
  subtext = 'Let our experts guide you through every step. Get your personalized consultation today.',
}: ServiceCTAProps) {
  return (
    <section className="relative py-16 md:py-24 lg:py-32 bg-gradient-to-br from-primary to-primary-dark overflow-hidden">
      {/* Background Video */}
      <div className="absolute inset-0 z-0">
        <video
          src="/videos/CTA.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover opacity-15"
        />
      </div>

      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[length:50px_50px]" />
      </div>

      {/* Animated Dots */}
      <motion.div
        className="absolute top-10 left-10 w-20 h-20 rounded-full bg-white/5"
        animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 180] }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-20 right-10 w-32 h-32 rounded-full bg-white/5"
        animate={{ scale: [1.2, 1, 1.2], rotate: [180, 90, 0] }}
        transition={{ duration: 10, repeat: Infinity }}
      />

      <div className="relative max-w-4xl mx-auto px-6 text-center z-10">
        {/* Heading */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="font-display text-4xl md:text-5xl text-white mb-6 leading-tight"
        >
          {heading}
        </motion.h2>

        {/* Subtext */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.6 }}
          viewport={{ once: true }}
          className="text-xl text-white/90 mb-10 max-w-2xl mx-auto leading-relaxed"
        >
          {subtext}
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
        >
          <Button
            variant="secondary"
            size="lg"
            className="bg-white text-primary hover:bg-white/90 border-white"
          >
            Get Free Consultation
          </Button>
          <button className="inline-flex items-center gap-2 px-6 py-3 h-12 rounded-sm bg-accent text-white font-medium hover:bg-accent/90 transition-colors duration-300">
            <MessageCircle className="w-5 h-5" />
            WhatsApp Us
          </button>
        </motion.div>
      </div>
    </section>
  );
}
